// eslint-disable-next-line no-unused-vars
let isClosingModal = false // Flag to avoid double calls when closing modal

/**
 * Sets the closing modal flag to avoid double stop calls
 * Call this before closing a modal that contains videos
 */
export function setClosingModalFlag(value) {
  isClosingModal = value
}

/**
 * Helper to send commands to YouTube iframe API
 */
function sendYouTubeCommand(iframe, command) {
  if (!iframe || !iframe.contentWindow) return false

  try {
    const src = iframe.src || ''
    if (!src.includes('youtube.com')) {
      console.error(`No src youtube.com`)
      return false
    }
    const origin = new URL(src).origin
    iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: command }), origin)
    return true
  } catch (e) {
    console.error('Error sending postMessage to YouTube iframe:', e)
    return false
  }
}

/**
 * Stops playback of a single video element (HTML5 <video>, direct YouTube iframe, or lite-youtube)
 * If resetToBeginning = true → full reset (used for modal close)
 * If resetToBeginning = false → pause only (used for slide/tab change)
 *
 * @param {HTMLElement} videoElement - The video element to stop
 * @param {boolean} resetToBeginning - If true, reset playback to the beginning and reset component
 */
export function stopVideoPlayback(videoElement, resetToBeginning = false) {
  if (!videoElement) return

  // HTML5 <video>
  if (videoElement.tagName.toLowerCase() === 'video') {
    try {
      videoElement.pause()
      if (resetToBeginning) videoElement.currentTime = 0
    } catch (e) {
      console.error('Error stopping HTML5 video:', e)
    }
    return
  }

  // lite-youtube
  if (videoElement.tagName.toLowerCase() === 'lite-youtube') {
    try {
      const shadowRoot = videoElement.shadowRoot
      if (shadowRoot) {
        const iframe = shadowRoot.querySelector('iframe')
        if (iframe) {
          const command = resetToBeginning ? 'stopVideo' : 'pauseVideo'
          sendYouTubeCommand(iframe, command)
        }
      }

      if (resetToBeginning) {
        // Full reset: replace element with a fresh clone
        const clone = videoElement.cloneNode(false) // shallow clone without children
        for (const attr of videoElement.attributes) {
          clone.setAttribute(attr.name, attr.value)
        }
        videoElement.parentNode.replaceChild(clone, videoElement)
      }
    } catch (e) {
      console.error('Error stopping lite-youtube:', e)
    }
    return
  }

  // Direct YouTube iframe
  if (
    videoElement.tagName.toLowerCase() === 'iframe' &&
    videoElement.src &&
    videoElement.src.includes('youtube.com/embed')
  ) {
    try {
      const parent = videoElement.parentNode
      const isInsideLiteYoutube = parent && parent.host && parent.host.tagName?.toLowerCase() === 'lite-youtube'

      if (isInsideLiteYoutube) {
        return
      }

      const command = resetToBeginning ? 'stopVideo' : 'pauseVideo'
      sendYouTubeCommand(videoElement, command)

      if (resetToBeginning) {
        videoElement.src = ''
      }
    } catch (e) {
      console.error('Error stopping direct YouTube iframe:', e)
    }
    return
  }

  console.error('Unrecognized video element type for stopping:', videoElement)
}

/**
 * Stops all videos inside a given container
 * @param {HTMLElement|Document} container - The container to search for videos
 * @param {boolean} resetToBeginning - If true, reset playback to the beginning and reset component
 */
export function stopAllVideosInContainer(container, resetToBeginning = false) {
  if (!container) return

  const processed = new Set()
  const videoElements = container.querySelectorAll('lite-youtube, video, iframe[src*="youtube.com/embed"]')

  videoElements.forEach(videoElement => {
    if (!processed.has(videoElement)) {
      stopVideoPlayback(videoElement, resetToBeginning)
      processed.add(videoElement)
    }
  })
}

/**
 * Handles video playback status when changing slides in a Swiper instance.
 * Pauses videos in non-active slides without resetting them, so they can resume later.
 *
 * @param {Object} swiper - The Swiper instance that contains the slides.
 * @param {string} videoSelector - The CSS selector to find video elements in slides.
 */
export function handlerSwiperStatusVideo(swiper, videoSelector) {
  const activeIndex = swiper.activeIndex
  swiper.slides.forEach((slide, index) => {
    const videoElement = slide.querySelector(videoSelector)

    if (videoElement) {
      let isActivated = false

      if (videoElement.tagName.toLowerCase() === 'lite-youtube') {
        const frameDiv = videoElement.shadowRoot?.querySelector('#frame')
        if (frameDiv?.classList.contains('activated')) {
          isActivated = true
        }
      } else if (videoElement.tagName.toLowerCase() === 'iframe') {
        isActivated = true
      }

      if (isActivated && index !== activeIndex) {
        stopVideoPlayback(videoElement, false) // false = pause only
      }
    }
  })
}

/**
 * Transforms a given element into a lite-youtube component.
 *
 * @param {HTMLElement} element - The DOM element to transform.
 */
export function transformToLiteYoutube(element) {
  const imgContainer = element

  if (!(imgContainer instanceof HTMLElement)) {
    return
  }

  const videoId = imgContainer.dataset.videoId
  if (!videoId) return

  if (imgContainer.querySelector('lite-youtube')) return

  const currentWidth = imgContainer.clientWidth
  const currentHeight = imgContainer.clientHeight

  const liteYoutube = document.createElement('lite-youtube')
  liteYoutube.setAttribute('videoid', videoId)
  liteYoutube.setAttribute('playlabel', 'Reproducir vídeo')
  liteYoutube.setAttribute('posterquality', 'hqdefault')
  liteYoutube.setAttribute('params', 'enablejsapi=1&rel=0')
  liteYoutube.setAttribute('disablenoscript', '')

  liteYoutube.addEventListener('liteYoutubeIframeLoaded', e => {
    // eslint-disable-next-line no-console
    console.log('YouTube iframe loaded:', e.detail.videoId)
  })

  imgContainer.innerHTML = ''
  imgContainer.appendChild(liteYoutube)

  liteYoutube.style.width = '100%'
  liteYoutube.style.height = '100%'
  liteYoutube.style.display = 'block'

  imgContainer.style.width = `${currentWidth}px`
  imgContainer.style.height = `${currentHeight}px`
  imgContainer.style.position = 'relative'
  imgContainer.style.overflow = 'hidden'
}
